import React from "react";
import { format } from "date-fns";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import {
  HiOutlineLocationMarker,
  HiOutlineCheckCircle,
  HiOutlineXCircle,
  HiOutlineClock,
  HiOutlineLightningBolt,
  HiOutlineChartPie,
} from "react-icons/hi";
import { Card, StatCard, Badge, Spinner } from "../UI";
import { VISIT_TYPE_MAP, VISIT_STATUSES } from "../../pages/Visits/visitConstants";
import { fmtMins, UtilisationBar } from "./VisitProductivity";

const Empty = ({ text }) => <p className="py-10 text-center text-sm text-gray-500">{text}</p>;

/**
 * Reports & Analytics → Visits tab. Data: GET /telecalling/reports/visits
 * ({ summary, byEmployee, daily, outcomes, byType, recent }).
 * chart = { theme, tooltipContentStyle, tooltipItemStyle, tooltipLabelStyle }
 */
const VisitsReportTab = ({ data, loading, chart }) => {
  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Spinner size="lg" />
      </div>
    );
  }
  const s = data?.summary;
  if (!s || (s.scheduled === 0 && s.rescheduled === 0 && (data.byEmployee || []).length === 0)) {
    return (
      <Card padding="p-8">
        <Empty text="No visits or meetings in the selected period." />
      </Card>
    );
  }

  const tooltipProps = {
    contentStyle: chart.tooltipContentStyle,
    itemStyle: chart.tooltipItemStyle,
    labelStyle: chart.tooltipLabelStyle,
    cursor: { fill: chart.theme.cursorFill },
  };
  const axis = { tick: { fill: chart.theme.tick, fontSize: 12 }, axisLine: false, tickLine: false };
  const daily = (data.daily || []).map((d) => ({ ...d, label: format(new Date(`${d.date}T00:00:00`), "dd MMM") }));

  return (
    <div className="space-y-6">
      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-6 gap-4">
        <StatCard
          title="Visits Scheduled"
          value={s.scheduled}
          icon={<HiOutlineLocationMarker className="w-6 h-6 text-white" />}
          color="primary"
        />
        <StatCard
          title="Completed"
          value={`${s.completed} (${s.completionRate}%)`}
          icon={<HiOutlineCheckCircle className="w-6 h-6 text-white" />}
          color="success"
        />
        <StatCard
          title="No Show / Cancelled"
          value={s.noShow + s.cancelled}
          icon={<HiOutlineXCircle className="w-6 h-6 text-white" />}
          color="danger"
        />
        <StatCard
          title="Time on Visits"
          value={fmtMins(s.actualMinutes)}
          icon={<HiOutlineClock className="w-6 h-6 text-white" />}
          color="info"
        />
        <StatCard
          title="Productive Time"
          value={fmtMins(s.productiveMinutes)}
          icon={<HiOutlineLightningBolt className="w-6 h-6 text-white" />}
          color="secondary"
        />
        <StatCard
          title="Avg Utilisation"
          value={s.avgUtilisation === null ? "—" : `${s.avgUtilisation}%`}
          icon={<HiOutlineChartPie className="w-6 h-6 text-white" />}
          color="warning"
        />
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          ["Avg visit length", fmtMins(s.avgVisitMinutes)],
          ["Started on time", s.completed + s.inProgress ? `${s.onTimeRate}%` : "—"],
          ["Repeat visits", s.repeatVisits],
          ["Upcoming / pending approval", `${s.upcoming} / ${s.pendingApproval}`],
        ].map(([label, value]) => (
          <Card key={label} padding="p-3">
            <p className="text-xs text-gray-500">{label}</p>
            <p className="text-lg font-bold text-gray-900">{value}</p>
          </Card>
        ))}
      </div>

      {/* Trend + outcomes */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card padding="p-4 sm:p-6" className="lg:col-span-2">
          <h3 className="font-semibold text-gray-900 mb-4">Visits per Day</h3>
          {daily.length === 0 ? (
            <Empty text="No visits in this period" />
          ) : (
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={daily}>
                <CartesianGrid strokeDasharray="3 3" stroke={chart.theme.grid} vertical={false} />
                <XAxis dataKey="label" {...axis} />
                <YAxis allowDecimals={false} {...axis} />
                <Tooltip {...tooltipProps} />
                <Legend wrapperStyle={{ color: chart.theme.legend, fontSize: 12 }} />
                <Bar dataKey="completed" name="Completed" stackId="v" fill="#16A34A" radius={[0, 0, 0, 0]} />
                <Bar dataKey="missed" name="No show / cancelled" stackId="v" fill="#DC2626" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </Card>

        <Card padding="p-4 sm:p-6">
          <h3 className="font-semibold text-gray-900 mb-4">Visit Outcomes</h3>
          {(data.outcomes || []).length === 0 ? (
            <Empty text="No completed visits yet" />
          ) : (
            <>
              <ResponsiveContainer width="100%" height={180}>
                <PieChart>
                  <Pie data={data.outcomes} dataKey="count" nameKey="label" innerRadius={45} outerRadius={75} paddingAngle={2}>
                    {data.outcomes.map((o) => (
                      <Cell key={o.key} fill={o.color} />
                    ))}
                  </Pie>
                  <Tooltip {...tooltipProps} />
                </PieChart>
              </ResponsiveContainer>
              <ul className="space-y-1.5 mt-2">
                {data.outcomes.map((o) => (
                  <li key={o.key} className="flex items-center justify-between text-sm">
                    <span className="flex items-center gap-2 text-gray-700">
                      <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: o.color }} />
                      {o.label}
                    </span>
                    <span className="font-semibold text-gray-900">
                      {o.count}
                      <span className="text-gray-400 font-normal"> · {s.completed ? Math.round((o.count / s.completed) * 100) : 0}%</span>
                    </span>
                  </li>
                ))}
              </ul>
            </>
          )}
        </Card>
      </div>

      {/* Types */}
      {(data.byType || []).length > 0 && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {data.byType.map((t) => {
            const type = VISIT_TYPE_MAP[t.type] || VISIT_TYPE_MAP.site_visit;
            const Icon = type.icon;
            return (
              <Card key={t.type} padding="p-3">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${type.color}1A` }}>
                    <Icon className="w-5 h-5" style={{ color: type.color }} />
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-gray-900">{type.label}</p>
                    <p className="text-xs text-gray-500">
                      {t.completed}/{t.count} done · {fmtMins(t.minutes)}
                    </p>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {/* Employees */}
      <Card padding="p-4 sm:p-6">
        <h3 className="font-semibold text-gray-900 mb-4">Employee Visits & Productivity</h3>
        {(data.byEmployee || []).length === 0 ? (
          <Empty text="No employee activity" />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-gray-500 border-b">
                  {["Employee", "Done / Scheduled", "Missed", "Visit Time", "Avg Visit", "On Time", "Outcomes", "Talk Time", "Productive", "Utilisation"].map(
                    (h) => (
                      <th key={h} className="py-2 pr-4 font-medium whitespace-nowrap">
                        {h}
                      </th>
                    )
                  )}
                </tr>
              </thead>
              <tbody>
                {data.byEmployee.map((r) => {
                  const v = r.visits;
                  const p = r.productivity;
                  return (
                    <tr key={r.id} className="border-b border-gray-100">
                      <td className="py-2.5 pr-4 whitespace-nowrap font-medium text-gray-900">{r.name}</td>
                      <td className="py-2.5 pr-4 whitespace-nowrap text-gray-700">
                        <b>{v.completed}</b> / {v.scheduled}
                        <span className="text-gray-400"> ({v.completionRate}%)</span>
                      </td>
                      <td className="py-2.5 pr-4 whitespace-nowrap text-red-600">{v.noShow + v.cancelled || "-"}</td>
                      <td className="py-2.5 pr-4 whitespace-nowrap text-gray-700">{fmtMins(v.actualMinutes)}</td>
                      <td className="py-2.5 pr-4 whitespace-nowrap text-gray-700">
                        {v.completed ? fmtMins(v.actualMinutes / v.completed) : "-"}
                      </td>
                      <td className="py-2.5 pr-4 whitespace-nowrap text-gray-700">{v.onTime + v.late ? `${v.onTimeRate}%` : "-"}</td>
                      <td className="py-2.5 pr-4 min-w-[180px]">
                        <div className="flex flex-wrap gap-1">
                          {Object.values(v.outcomes || {}).map((o) => (
                            <span
                              key={o.label}
                              className="px-1.5 py-0.5 rounded text-[11px] font-semibold border whitespace-nowrap"
                              style={{ color: o.color, borderColor: `${o.color}55`, backgroundColor: `${o.color}12` }}
                            >
                              {o.label} {o.count}
                            </span>
                          ))}
                          {!Object.keys(v.outcomes || {}).length && <span className="text-gray-400">-</span>}
                        </div>
                      </td>
                      <td className="py-2.5 pr-4 whitespace-nowrap text-gray-700">{fmtMins(p.talkMinutes)}</td>
                      <td className="py-2.5 pr-4 whitespace-nowrap font-semibold text-gray-900">{fmtMins(p.productiveMinutes)}</td>
                      <td className="py-2.5">
                        <UtilisationBar value={p.utilisation} />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Recent visits */}
      <Card padding="p-4 sm:p-6">
        <h3 className="font-semibold text-gray-900 mb-4">Visit Details</h3>
        {(data.recent || []).length === 0 ? (
          <Empty text="No visits" />
        ) : (
          <div className="overflow-x-auto max-h-[480px]">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-white">
                <tr className="text-left text-gray-500 border-b">
                  {["Date & Time", "Employee", "Customer", "Type", "Status", "Planned", "Actual", "Start", "Outcome"].map((h) => (
                    <th key={h} className="py-2 pr-4 font-medium whitespace-nowrap">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.recent.map((v) => {
                  const type = VISIT_TYPE_MAP[v.type] || VISIT_TYPE_MAP.site_visit;
                  const status = VISIT_STATUSES[v.status] || VISIT_STATUSES.scheduled;
                  return (
                    <tr key={v._id} className="border-b border-gray-100">
                      <td className="py-2.5 pr-4 whitespace-nowrap text-gray-700">{format(new Date(v.date), "dd MMM, hh:mm a")}</td>
                      <td className="py-2.5 pr-4 whitespace-nowrap text-gray-700">{v.employee}</td>
                      <td className="py-2.5 pr-4 whitespace-nowrap font-medium text-gray-900">
                        {v.lead?.name || "-"}
                        {v.repeat && <span className="ml-1.5 text-[11px] text-primary-600 font-semibold">Repeat</span>}
                      </td>
                      <td className="py-2.5 pr-4 whitespace-nowrap text-gray-700">{type.label}</td>
                      <td className="py-2.5 pr-4 whitespace-nowrap">
                        <Badge variant={status.variant}>{status.label}</Badge>
                      </td>
                      <td className="py-2.5 pr-4 whitespace-nowrap text-gray-700">{fmtMins(v.plannedMinutes)}</td>
                      <td className="py-2.5 pr-4 whitespace-nowrap text-gray-700">{v.status === "completed" ? fmtMins(v.actualMinutes) : "-"}</td>
                      <td className="py-2.5 pr-4 whitespace-nowrap">
                        {v.lateMinutes === null ? (
                          <span className="text-gray-400">-</span>
                        ) : v.lateMinutes <= 0 ? (
                          <span className="text-green-600">On time</span>
                        ) : (
                          <span className="text-orange-600">{fmtMins(v.lateMinutes)} late</span>
                        )}
                      </td>
                      <td className="py-2.5 whitespace-nowrap text-gray-700">{v.outcome || "-"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
};

export default VisitsReportTab;
